import {
  CLOUD_COVERAGE_OPTIONS,
  GRID_MARKET_MODE,
  SWITCHER_ICON_SIZES,
} from 'src/constants/application';
import { GridFeeType, SpotMarketType } from 'src/graphql';
import {
  TAssetFieldTemplatesArgs,
  TFieldsTemplateUnion,
  TNumberFieldTemplate,
  TTextFieldTemplate,
} from 'src/utils/assetsFields/assetsFields.types';

import { EFormVariant } from 'src/typings/base-types';

// Template of node's fields. They are listed in order in which they should appear in form.

/* Defaults */
const defaultValues = {
  fitToLimit: true,
};

/*
 * Fields used to show API / Properties tabs
 * Shared between all entities
 */
// const KPI_API_PROPERTIES_FIELDS = ({
//   kpis,
//   type,
//   values = {},
//   configType,
//   isLibrary,
// }: Pick<TAssetFieldTemplatesArgs, 'type' | 'values' | 'configType' | 'isLibrary'> & {
//   kpis:
//     | NonNullable<TSettingsData['kpis']>['Area']
//     | NonNullable<TSettingsData['kpis']>['Device'];
// }): TFieldsTemplateUnion[] => {
//   if (isLibrary) return [];

//   const output: TFieldsTemplateUnion[] = [
//     {
//       name: 'allowExternalConnection',
//       type: 'switcher',
//       options: [
//         { icon: 'check-mark', iconSize: iconSize.tick, value: true },
//         { icon: 'close', iconSize: iconSize.close, value: false },
//       ],
//       label: 'API open',
//       formView: EFormVariant.KpiSettings,
//     },
//     {
//       name: 'targetKpi',
//       type: 'enum',
//       label: 'Select KPI',
//       options: [...kpis],
//       formView: EFormVariant.KpiSettings,
//     },
//   ];

//   if (['Load', 'PV'].includes(type)) {
//     if (configType === ConfigType.CanaryNetwork) {
//       output.push({
//         name: 'forecastStreamEnabled',
//         type: 'switcher',
//         options: [
//           { icon: 'check-mark', iconSize: iconSize.tick, value: false },
//           { icon: 'close', iconSize: iconSize.close, value: true },
//         ],
//         label: 'Live data',
//         formView: EFormVariant.KpiSettings,
//         disabled: !values.allowExternalConnection,
//       });
//     }
//   }

//   return output;
// };

// In most cases, these will be the fields which should be displayed when viewing
// a new or existing node.
function baseFieldTemplates({
  type,
  settingsData,
  values = {},
  isLibrary,
  configType,
  configurationCharacteristic,
}: TAssetFieldTemplatesArgs): TFieldsTemplateUnion[] {
  // const evaluatedDeviceKpisFields = KPI_API_PROPERTIES_FIELDS({
  //   kpis: 'kpis' in settingsData ? settingsData.kpis.Device : [],
  //   type,
  //   values,
  //   configType,
  //   isLibrary,
  // });
  // const evaluatedMarketKpisFields = KPI_API_PROPERTIES_FIELDS({
  //   kpis: 'kpis' in settingsData ? settingsData.kpis.Area : [],
  //   type,
  //   values,
  //   configType,
  //   isLibrary,
  // });

  switch (type) {
    /* Area */
    case 'Area':
      return [
        {
          name: 'libraryUUID',
          formView: EFormVariant.Express,
        },
        // ...evaluatedMarketKpisFields,
        {
          name: 'name',
          type: 'text',
          label: 'Name',
          formView: EFormVariant.Express,
        },
        // TODO: REFACTOR IN PROGRESS, fitAreaBoundary must be added at transformToAreaInput step
        {
          name: 'geoTagLocation',
          type: 'location',
          label: 'Geo-tag',
          tooltipText: 'Parent market already has location',
          EXCLUDE: isLibrary,
          formView: EFormVariant.Express,
        },
        // TODO:  It will not be implemented in v1 of Phoenix but we will need it later.
        // TODO: https://gridsingularity.atlassian.net/jira/software/projects/PH/boards/41?label=Airnauts&selectedIssue=PH-124
        // {
        //   name: 'count',
        //   type: 'counter',
        //   label: 'Multiply same unit',
        //   min: 1,
        //   formView: EFormVariant.Express,
        // },
        /*
          TODO: Do we need geoTagType in phoenix?
          If yes, fix the issue: Area -> advanced -> change geo-tag-type to location -> switch between express and advanced. 
        */
        // {
        //   name: 'geoTagType',
        //   type: 'enum',
        //   label: 'Geo-tag Type',
        //   options: [
        //     { value: 'area', label: 'Area' },
        //     { value: 'location', label: 'Location' },
        //   ],
        //   tooltipText: 'Parent market already has location',
        //   EXCLUDE: isLibrary,
        //   formView: EFormVariant.Advanced,
        // },
        {
          name: 'exchangeInformation',
          type: 'enum',
          label: 'Exchange Information',
          options: [{ value: 'spot', label: 'Spot' }],
          disabled: true,
          EXCLUDE: isLibrary,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'gridFeeEnabled',
          type: 'switcher',
          options: [
            { icon: 'close', iconSize: SWITCHER_ICON_SIZES.close, value: false },
            { icon: 'check-mark', iconSize: SWITCHER_ICON_SIZES.tick, value: true },
          ],
          label: 'Grid fees',
          EXCLUDE: isLibrary,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'gridFeePercentage',
          type: 'number',
          label: 'Grid fee value',
          unit: '%',
          EXCLUDE:
            isLibrary ||
            !values.gridFeeEnabled ||
            settingsData.gridFeeType !== GridFeeType.Percentage,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'gridFeeConstant',
          type: 'number',
          label: 'Grid fee value',
          unit: 'cents / kWh',
          EXCLUDE:
            isLibrary ||
            !values.gridFeeEnabled ||
            settingsData.gridFeeType !== GridFeeType.Constant,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'transformerCapacityEnabled',
          type: 'switcher',
          options: [
            { icon: 'close', iconSize: SWITCHER_ICON_SIZES.close, value: false },
            { icon: 'check-mark', iconSize: SWITCHER_ICON_SIZES.tick, value: true },
          ],
          label: 'Transformer capacity',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'importCapacityKva',
          type: 'number',
          label: 'Import',
          unit: 'kVA',
          inlineAlign: 'left',
          EXCLUDE: !values.transformerCapacityEnabled,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'exportCapacityKva',
          type: 'number',
          label: 'Export',
          unit: 'kVA',
          inlineAlign: 'right',
          EXCLUDE: !values.transformerCapacityEnabled,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'baselinePeakEnergyEnabled',
          type: 'switcher',
          options: [
            { icon: 'close', iconSize: SWITCHER_ICON_SIZES.close, value: false },
            { icon: 'check-mark', iconSize: SWITCHER_ICON_SIZES.tick, value: true },
          ],
          label: 'Energy peak percentage',
          EXCLUDE: isLibrary,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'baselinePeakEnergyImportKwh',
          type: 'number',
          label: 'Baseline peak import',
          unit: 'kWh',
          inlineAlign: 'left',
          EXCLUDE: isLibrary || !values.baselinePeakEnergyEnabled,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'baselinePeakEnergyExportKwh',
          type: 'number',
          label: 'Baseline peak export',
          unit: 'kWh',
          inlineAlign: 'right',
          EXCLUDE: isLibrary || !values.baselinePeakEnergyEnabled,
          formView: EFormVariant.Advanced,
        },
      ];

    /* FiniteDieselGenerator */
    case 'FiniteDieselGenerator':
      return [
        {
          name: 'libraryUUID',
          formView: EFormVariant.Express,
        },
        {
          name: 'name',
          type: 'text',
          label: 'Name',
          formView: EFormVariant.Express,
        },
        {
          name: 'geoTagLocation',
          type: 'location',
          label: 'Geo-tag',
          tooltipText: 'Parent market already has location',
          EXCLUDE: isLibrary,
          formView: EFormVariant.Express,
        },
        // {
        //   name: 'count',
        //   type: 'counter',
        //   label: 'Multiply same unit',
        //   min: 1,
        //   formView: EFormVariant.Express,
        // },
        {
          name: 'energyRate',
          type: 'number',
          label: 'Energy rate',
          unit: 'cents / kWh',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'maxAvailablePowerKw',
          type: 'number',
          label: 'Maximum available power',
          unit: 'kW',
          formView: EFormVariant.Advanced,
        },
      ];

    /* Load */
    case 'Load':
      return [
        {
          name: 'libraryUUID',
          formView: EFormVariant.Express,
        },
        // ...evaluatedDeviceKpisFields,
        {
          name: 'name',
          type: 'text',
          label: 'Name',
          formView: EFormVariant.Express,
        },
        {
          name: 'geoTagLocation',
          type: 'location',
          label: 'Geo-tag',
          tooltipText: 'Parent market already has location',
          EXCLUDE: isLibrary,
          formView: EFormVariant.Express,
        },
        // {
        //   name: 'count',
        //   type: 'counter',
        //   label: 'Multiply same unit',
        //   min: 1,
        //   formView: EFormVariant.Express,
        // },
        {
          name: 'loadProfileOption',
          type: 'enum',
          label: 'Load profile',
          options: [
            { value: 'userConfigure', label: 'User configure profile' },
            { value: 'userUpload', label: 'User upload profile' },
          ],
          formView: EFormVariant.Advanced,
        },
        {
          name: 'avgPowerW',
          type: 'number',
          label: 'Average power',
          unit: 'W',
          formView: EFormVariant.Advanced,
          EXCLUDE: values.loadProfileOption !== 'userConfigure',
        },
        {
          name: 'hrsPerDay',
          type: 'slider',
          label: 'Hours per day',
          step: 1,
          // labelStep: 2,
          minVal: 0,
          maxVal: 24,
          formView: EFormVariant.Advanced,
          EXCLUDE: values.loadProfileOption !== 'userConfigure',
        },
        {
          name: 'hrsOfDay',
          type: 'slider',
          label: 'Hours of day',
          step: 1,
          // labelStep: 2,
          minVal: 0,
          maxVal: 23,
          formView: EFormVariant.Advanced,
          EXCLUDE: values.loadProfileOption !== 'userConfigure',
        },
        {
          name: 'dailyLoadProfile',
          type: 'file',
          label: 'Upload profile',
          formView: EFormVariant.Advanced,
          EXCLUDE: values.loadProfileOption !== 'userUpload',
        },
        {
          name: 'dailyLoadProfileUuid',
          formView: EFormVariant.Express,
          EXCLUDE: values.loadProfileOption !== 'userUpload',
        },
        {
          name: 'initialBuyingRate',
          type: 'number',
          label: 'Initial buying rate',
          unit: 'cents / kWh',
          EXCLUDE: settingsData.spotMarketType === SpotMarketType.OneSided,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'useMarketMakerRate',
          type: 'enum',
          label: 'Final buying rate',
          options: [
            { value: true, label: 'Market maker rate' },
            { value: false, label: 'User input' },
          ],
          formView: EFormVariant.Advanced,
        },
        {
          name: 'finalBuyingRate',
          type: 'number',
          unit: 'cents / kWh',
          formView: EFormVariant.Advanced,
          EXCLUDE: values.useMarketMakerRate,
        },
        {
          name: 'energyRateIncreasePerUpdate',
          type:
            configurationCharacteristic.gridMakerHasUploadedProfile && values.fitToLimit
              ? 'text'
              : 'number',
          label: 'Rate increase',
          unit: 'cents / kWh per update',
          disabled: configurationCharacteristic.gridMakerHasUploadedProfile && values.fitToLimit,
          EXCLUDE: settingsData.spotMarketType === SpotMarketType.OneSided,
          formView: EFormVariant.Advanced,
        } as TNumberFieldTemplate | TTextFieldTemplate,
        {
          name: 'fitToLimit',
          type: 'checkbox',
          label: 'Fit to limits',
          EXCLUDE: settingsData.spotMarketType === SpotMarketType.OneSided,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'updateInterval',
          type: 'slider',
          label: 'Update interval',
          unit: 'min',
          showUnit: true,
          step: 1,
          // labelStep: settingsData.slotLengthMinutes - 2,
          minVal: 1,
          maxVal: settingsData.slotLengthMinutes - 1,
          EXCLUDE: settingsData.spotMarketType === SpotMarketType.OneSided,
          formView: EFormVariant.Advanced,
        },
      ];

    /* PV */
    case 'PV':
      return [
        // ...evaluatedDeviceKpisFields,
        {
          name: 'name',
          type: 'text',
          label: 'Name',
          formView: EFormVariant.Express,
        },
        {
          name: 'geoTagLocation',
          type: 'location',
          label: 'Geo-tag',
          tooltipText: 'Parent market already has location',
          EXCLUDE: isLibrary,
          formView: EFormVariant.Express,
        },
        // {
        //   name: 'count',
        //   type: 'counter',
        //   label: 'Multiply same unit',
        //   min: 1,
        //   formView: EFormVariant.Express,
        // },
        {
          name: 'capacityKw',
          type: 'number',
          label: 'Capacity',
          formView: EFormVariant.Advanced,
          unit: 'kW',
        },
        {
          name: 'cloudCoverage',
          type: 'enum',
          label: 'Solar Profile',
          options: CLOUD_COVERAGE_OPTIONS,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'powerProfile',
          type: 'file',
          label: 'Override profile',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'powerProfileUuid',
          formView: EFormVariant.Express,
        },
        {
          name: 'libraryUUID',
          formView: EFormVariant.Express,
        },
        {
          name: 'useMarketMakerRate',
          type: 'enum',
          label: 'Initial selling rate',
          options: [
            { value: true, label: 'Market maker rate' },
            { value: false, label: 'User input' },
          ],
          formView: EFormVariant.Advanced,
        },
        {
          name: 'initialSellingRate',
          type: 'number',
          unit: 'cents / kWh',
          formView: EFormVariant.Advanced,
          EXCLUDE: values.useMarketMakerRate,
        },
        {
          name: 'finalSellingRate',
          type: 'number',
          label: 'Final selling rate',
          unit: 'cents / kWh',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'energyRateDecreasePerUpdate',
          type:
            configurationCharacteristic.gridMakerHasUploadedProfile && values.fitToLimit
              ? 'text'
              : 'number',
          label: 'Rate decrease',
          unit: 'cents / kWh per update',
          disabled: configurationCharacteristic.gridMakerHasUploadedProfile && values.fitToLimit,
          formView: EFormVariant.Advanced,
        } as TNumberFieldTemplate | TTextFieldTemplate,
        {
          name: 'fitToLimit',
          type: 'checkbox',
          label: 'Fit to limits',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'updateInterval',
          type: 'slider',
          label: 'Update interval',
          unit: 'min',
          showUnit: true,
          step: 1,
          // labelStep: settingsData.slotLengthMinutes - 2,
          minVal: 1,
          maxVal: settingsData.slotLengthMinutes - 1,
          formView: EFormVariant.Advanced,
        },
      ];

    /* Storage */
    case 'Storage':
      return [
        {
          name: 'libraryUUID',
          formView: EFormVariant.Express,
        },
        {
          name: 'name',
          type: 'text',
          label: 'Name',
          formView: EFormVariant.Express,
        },
        {
          name: 'geoTagLocation',
          type: 'location',
          label: 'Geo-tag',
          tooltipText: 'Parent market already has location',
          EXCLUDE: isLibrary,
          formView: EFormVariant.Express,
        },
        // {
        //   name: 'count',
        //   type: 'counter',
        //   label: 'Multiply same unit',
        //   min: 1,
        //   formView: EFormVariant.Express,
        // },
        // ...evaluatedDeviceKpisFields,
        {
          name: 'batteryCapacityKwh',
          type: 'number',
          label: 'Battery capacity',
          unit: 'kWh',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'initialSoc',
          type: 'number',
          label: 'Initial capacity',
          unit: '%',
          inlineAlign: 'left',
          fullWidthErr: true,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'initialkWh',
          type: 'number',
          unit: 'kWh',
          inlineAlign: 'right',
          disabled: true,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'minAllowedSoc',
          type: 'number',
          label: 'Minimum state of charge',
          unit: '%',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'maxAbsBatteryPowerKw',
          type: 'number',
          label: 'Max power rating for battery',
          unit: 'kW',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'initialSellingRate',
          type: 'number',
          label: 'Initial selling rate',
          unit: 'cents / kWh',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'finalSellingRate',
          type: 'number',
          label: 'Final selling rate',
          unit: 'cents / kWh',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'energyRateDecreasePerUpdate',
          type: 'number',
          label: 'Rate decrease',
          unit: 'cents / kWh per update',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'initialBuyingRate',
          type: 'number',
          label: 'Initial buying rate',
          unit: 'cents / kWh',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'finalBuyingRate',
          type: 'number',
          label: 'Final buying rate',
          unit: 'cents / kWh',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'energyRateIncreasePerUpdate',
          type: 'number',
          label: 'Rate increase',
          unit: 'cents / kWh per update',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'fitToLimit',
          type: 'checkbox',
          label: 'Fit to limits',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'updateInterval',
          type: 'slider',
          label: 'Update interval',
          unit: 'min',
          showUnit: true,
          step: 1,
          // labelStep: settingsData.slotLengthMinutes - 2,
          minVal: 1,
          maxVal: settingsData.slotLengthMinutes - 1,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'capPriceStrategy',
          type: 'checkbox',
          label: 'Capacity based method',
          formView: EFormVariant.Advanced,
        },
      ];

    /* MarketMaker */
    case 'MarketMaker':
      return [
        {
          name: 'name',
          type: 'text',
          label: 'Name',
          formView: EFormVariant.Express,
        },
        {
          name: GRID_MARKET_MODE,
          type: 'enum',
          label: 'Mode',
          options: [
            { value: 'MarketMakerMode', label: 'Infinite power plant' },
            { value: 'InfiniteBusMode', label: 'Infinite bus' },
          ],
          formView: EFormVariant.Advanced,
        },
        {
          name: 'gridConnected',
          type: 'enum',
          label: 'Role',
          options: [
            { value: true, label: 'Grid connected' },
            { value: false, label: 'Islanded' },
          ],
          formView: EFormVariant.Advanced,
        },
        {
          name: 'energyRateType',
          type: 'enum',
          label: 'Selling rate type',
          options: [
            { value: 0, label: 'User Input' },
            { value: 1, label: 'File Upload' },
          ],
          formView: EFormVariant.Advanced,
        },
        {
          name: 'energyRateProfile',
          type: 'file',
          label: 'Selling rate profile',
          placeholder: 'Choose a file',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'energyRate',
          type: 'number',
          label: 'Selling rate',
          unit: 'cents / kWh',
          formView: EFormVariant.Advanced,
          EXCLUDE: configurationCharacteristic.gridMakerHasUploadedProfile,
        },
        {
          name: 'energyRateProfileUuid',
          formView: EFormVariant.Express,
        },
        {
          name: 'libraryUUID',
          formView: EFormVariant.Express,
        },
      ];

    /* InfiniteBus */
    case 'InfiniteBus':
      return [
        {
          name: 'name',
          type: 'text',
          label: 'Name',
          formView: EFormVariant.Express,
        },
        {
          name: GRID_MARKET_MODE,
          type: 'enum',
          label: 'Mode',
          options: [
            { value: 'MarketMakerMode', label: 'Infinite power plant' },
            { value: 'InfiniteBusMode', label: 'Infinite bus' },
          ],
          formView: EFormVariant.Advanced,
        },
        {
          name: 'gridConnected',
          type: 'enum',
          label: 'Role',
          options: [
            { value: true, label: 'Grid connected' },
            { value: false, label: 'Islanded' },
          ],
          disabled: true,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'energyRateType',
          type: 'enum',
          label: 'Selling rate type',
          options: [
            { value: 0, label: 'User Input' },
            { value: 1, label: 'File Upload' },
          ],
          disabled: true,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'energyRateProfile',
          type: 'file',
          label: 'Selling rate profile',
          placeholder: 'Choose a file',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'energyRate',
          type: 'number',
          label: 'Selling rate',
          unit: 'cents / kWh',
          formView: EFormVariant.Advanced,
          EXCLUDE: configurationCharacteristic.gridMakerHasUploadedProfile,
        },
        {
          name: 'energyRateProfileUuid',
          formView: EFormVariant.Express,
        },
        {
          name: 'buyingRateType',
          type: 'enum',
          label: 'Buying rate type',
          options: [
            { value: 0, label: 'User Input' },
            { value: 1, label: 'File Upload' },
          ],
          disabled: true,
          formView: EFormVariant.Advanced,
        },
        {
          name: 'buyingRateProfile',
          type: 'file',
          label: 'Buying rate profile',
          placeholder: 'Choose a file',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'energyBuyRate',
          type: 'number',
          label: 'Buying rate',
          unit: 'cents / kWh',
          formView: EFormVariant.Advanced,
        },
        {
          name: 'buyingRateProfileUuid',
          formView: EFormVariant.Express,
        },
        {
          name: 'libraryUUID',
          formView: EFormVariant.Express,
        },
      ];

    default:
      throw new Error(`Invalid type passed to getAssetFieldTemplates function - ${type}`);
  }
}

export function getAllFieldTemplatesForAsset({
  values = defaultValues,
  ...args
}: TAssetFieldTemplatesArgs): ReturnType<typeof baseFieldTemplates> {
  return baseFieldTemplates({ values, ...args });
}

export function getSingleFieldTemplateForAsset({
  name,
  values = defaultValues,
  ...args
}: {
  name: string;
} & TAssetFieldTemplatesArgs): TFieldsTemplateUnion | undefined {
  return baseFieldTemplates({ values, ...args }).find((f) => f.name === name);
}
